import React from 'react';
import { useDataTableFilterContext } from '../../../../utils/DataTableFilterContext';
import { MenuList, MenuListItem, MenuListItemText, MenuListLabel } from '../../../../../MenuList';
import { MenuListItemIcon } from '../../../../../MenuList/MenuListItemIcon';
import { Dropdown } from '../../../../../Dropdown';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';
import { useHelperTextForRelativeDate } from '../../../../../DateRelativePicker/DateRelativePicker.utils';
import { useRelativeDateRangeTemplateLabel } from './impl/useRelativeDateRangeTemplateLabel';
import { PermanentFilterButton } from '../../view/PermanentFilterButton';
import { Divider } from '../../../../../Divider';
import { CheckIcon } from '../../view/CheckIcon';
import { QueryModel } from '../../../../utils/DataTableQueryProvider/DataTableQuery.types';
import { RelativeDateRangeModelInput } from './types';

interface RelativeDateRangeProps<Q extends Record<string, any>> {
  label: string;
  onChange: (date: RelativeDateRangeModelInput | undefined) => void;
  value: Partial<RelativeDateRangeModelInput> | undefined | null;
  dateTemplates?: Partial<RelativeDateRangeModelInput>[];
  scrollToFilterOnDrawerOpen?: keyof Q & string;
}

const BUILT_IN_TEMPLATES: Partial<RelativeDateRangeModelInput>[] = [
  {
    type: 'IS_IN_THE_LAST',
    timeUnit: 'DAY',
    amount: 1
  },
  {
    type: 'IS_IN_THE_LAST',
    timeUnit: 'DAY',
    amount: 7
  },
  {
    type: 'IS_IN_THE_LAST',
    timeUnit: 'DAY',
    amount: 30
  },
  {
    type: 'IS_IN_THE_LAST',
    timeUnit: 'DAY',
    amount: 90
  },
  {
    type: 'IS_IN_THE_LAST',
    timeUnit: 'DAY',
    amount: 365
  }
];

export function RelativeDateRange<Q extends QueryModel = QueryModel>({
  label,
  onChange,
  value,
  dateTemplates = BUILT_IN_TEMPLATES,
  scrollToFilterOnDrawerOpen
}: RelativeDateRangeProps<Q>) {
  const { setOpenFilterDrawer } = useDataTableFilterContext();
  const getHelperText = useHelperTextForRelativeDate();
  const getLabel = useRelativeDateRangeTemplateLabel();

  const hasValue = !!value?.type;
  const valueMatchesTemplate = !!dateTemplates?.filter((it) => isEqual(it, value)).length;
  return (
    <Dropdown
      ButtonItem={() => (
        <PermanentFilterButton
          label={label}
          valueLabel={hasValue ? (valueMatchesTemplate ? getLabel(value) : getHelperText(value)) : undefined}
          dataTest="permanent-filter-relative-date-range"
          onDelete={() => onChange(undefined)}
        />
      )}
    >
      <MenuList>
        <MenuListLabel>{label}</MenuListLabel>
        {dateTemplates.map((item) => {
          const selected = isEqual(item, value);
          return (
            <StyledMenuItem
              hasEndIcon
              selected={selected}
              key={JSON.stringify(item)}
              onClick={() => onChange(!selected ? (item as RelativeDateRangeModelInput) : undefined)}
            >
              <MenuListItemText primary={getLabel(item)} />
              <MenuListItemIcon>{selected && <CheckIcon />}</MenuListItemIcon>
            </StyledMenuItem>
          );
        })}
        {hasValue && !valueMatchesTemplate && (
          <>
            <Divider />
            <StyledMenuItem hasEndIcon selected onClick={() => setOpenFilterDrawer(true, scrollToFilterOnDrawerOpen)}>
              <MenuListItemText primary={getHelperText(value)} />
              <MenuListItemIcon>
                <CheckIcon />
              </MenuListItemIcon>
            </StyledMenuItem>
          </>
        )}
      </MenuList>
    </Dropdown>
  );
}

const StyledMenuItem = styled(MenuListItem)`
  && {
    min-width: 200px;
  }
`;
